import { useCallback, useMemo, useState } from "react";

import { oids } from "@/configs/Settings";
import { useCrmSetupConfiguration } from "../../setup/hooks/useCrmSetupConfiguration";
import { useCrmSetupEligibility } from "../../setup/hooks/useCrmSetupEligibility";
import type { CrmSetupStepId } from "../../setup/types";
import type { CrmStatisticsActiveTabId } from "../../statistics/types";
import type { CreateMessageTemplateInput, MessageTemplate } from "../../messageTemplates/types";
import { useMessageTemplates } from "../../messageTemplates/hooks/useMessageTemplates";
import { useWhatsAppEndpoint } from "../../messaging/hooks/useWhatsAppEndpoint";
import type { ClinicalCrmManagementData } from "../types";

export type UseClinicalCrmManagementDataProps = {
  /**
   * Identificador da organização (contexto do tenant).
   *
   * Fallback: quando não informado, usa o valor atual hardcoded para manter compatibilidade
   * com a implementação existente enquanto a origem real não é injetada.
   */
  organizationId?: string;
  /**
   * Identificador do practitioner (usuário/profissional).
   *
   * Fallback: quando não informado, usa o valor atual derivado de `oids.cpf`.
   */
  practitionerId?: string;
};

export function useClinicalCrmManagementData(
  props: UseClinicalCrmManagementDataProps = {},
): ClinicalCrmManagementData {
  const [activeTabId, setActiveTabId] =
    useState<CrmStatisticsActiveTabId>("messageTemplates");
  /**
   * A listagem espera a primeira abertura da seção de modelos.
   * Não volta para false ao recolher: o cache impede outra rede.
   */
  const [messageTemplatesSectionOpened, setMessageTemplatesSectionOpened] =
    useState(false);
  const markMessageTemplatesSectionOpened = useCallback(() => {
    setMessageTemplatesSectionOpened(true);
  }, []);

  /**
   * Importante:
   * - Evitar cair em fallback com string vazia, pois isso dispara efeitos cedo demais
   *   (ex.: templates buscando DEFAULT antes do status real do endpoint/contrato).
   * - Mantemos string quando ela de fato existe (trim), senão ficamos com `undefined`
   *   e aplicamos gating nos hooks dependentes.
   *
   * Observação:
   * - Este `organizationId` deve ser o mesmo usado pelo setup (step e stepper),
   *   caso contrário a seleção (ex. Fluxmed) é salva em um "tenant" e lida em outro,
   *   causando inconsistências.
   */
  const organizationId = props.organizationId?.trim() || undefined;
  const practitionerId = props.practitionerId?.trim() || undefined;

  const [currentOnboardingStepId, setCurrentOnboardingStepId] =
    useState<CrmSetupStepId>("introduction");

  const eligibility = useCrmSetupEligibility({ organizationId });

  const crmSetupConfiguration = useCrmSetupConfiguration({
    organizationId,
    hasFluxZapExtensionActive: eligibility.hasFluxZapExtensionActive,
  });

  const configurationStatus = crmSetupConfiguration.configurationStatus;
  const isCrmConfigurationLoading =
    crmSetupConfiguration.isLoadingConfiguration;

  const isCrmConfigurationAccountsConfigured = useMemo(() => {
    if (!configurationStatus) return false;
    const { selectedOption, metaConnection } = configurationStatus;
    // Fluxmed selecionado (por enquanto) ou Meta selecionado com payload retornado (WABA).
    return (
      selectedOption === "fluxmedDefault" || Boolean(metaConnection?.wabaId)
    );
  }, [configurationStatus]);

  const whatsappBusinessAccountId =
    configurationStatus?.metaConnection?.wabaId ?? undefined;
  const crmWhatsappWabaId = whatsappBusinessAccountId;
  const crmWhatsappChannelOption = configurationStatus?.selectedOption ?? null;

  const { phoneNumberIdRank1 } = useWhatsAppEndpoint({ organizationId });

  const resolvedPhoneNumberId = useMemo(() => {
    const fromEndpoint = String(phoneNumberIdRank1 ?? "").trim();
    if (fromEndpoint) return fromEndpoint;
    const fromMeta = String(
      configurationStatus?.metaConnection?.primaryPhoneNumberId ?? "",
    ).trim();
    if (fromMeta) return fromMeta;
    return (
      String(import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID ?? "").trim() ||
      "379190331933594"
    );
  }, [
    configurationStatus?.metaConnection?.primaryPhoneNumberId,
    phoneNumberIdRank1,
  ]);

  /**
   * Gating para evitar double-fetch:
   * - Enquanto `getStatus` não resolveu (loading + status null), não buscamos templates.
   * - Só habilitamos templates quando:
   *   - Fluxmed default (contrato existe), ou
   *   - Meta selecionado COM WABA (endpoint ativo / conexão persistida).
   * - Se selectedOption === null (nenhum endpoint/contrato): stepper aparece e não buscamos templates.
   */
  const isTemplatesEnabled = useMemo(() => {
    if (isCrmConfigurationLoading) return false;
    if (!configurationStatus) return false;

    if (configurationStatus.selectedOption === "fluxmedDefault") return true;

    if (configurationStatus.selectedOption === "metaEmbeddedSignup") {
      return Boolean(configurationStatus.metaConnection?.wabaId?.trim());
    }

    return false;
  }, [configurationStatus, isCrmConfigurationLoading]);

  const {
    templates: messageTemplates,
    isLoading: isMessageTemplatesLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refreshTemplates,
  } = useMessageTemplates({
    whatsappBusinessAccountId,
    phoneNumberId: resolvedPhoneNumberId,
    organizationId,
    // WABA/config continua no gate antigo. A abertura da seção é o segundo gate.
    enabled: isTemplatesEnabled && messageTemplatesSectionOpened,
  });

  const shouldShowStepper = eligibility.shouldShowStepper;

  const handleGoToStep = useCallback(async (stepId: CrmSetupStepId) => {
    setCurrentOnboardingStepId(stepId);
  }, []);

  const handleCompleteOnboarding = useCallback(async () => {
    eligibility.markOnboardingCompletedInSession();
    eligibility.refetch();
  }, [eligibility]);

  const handleCreateTemplate = useCallback(
    async (input: CreateMessageTemplateInput) => {
      await createTemplate({ input });
    },
    [createTemplate],
  );

  const handleUpdateTemplate = useCallback(
    async (input: MessageTemplate & { headerMediaFile?: File | null }) => {
      await updateTemplate({ input });
    },
    [updateTemplate],
  );

  const handleDeleteTemplate = useCallback(
    async (templateId: string) => {
      await deleteTemplate({ templateId });
    },
    [deleteTemplate],
  );

  const handleRefreshMessageTemplates = useCallback(async () => {
    await refreshTemplates();
  }, [refreshTemplates]);

  return {
    activeTabId,
    setActiveTabId,
    messageTemplates,
    isSetupLoading: eligibility.isLoading || isCrmConfigurationLoading,
    shouldShowStepper,
    currentOnboardingStepId,
    isCrmConfigurationAccountsConfigured,
    isCrmConfigurationLoading,
    organizationId: organizationId ?? "",
    practitionerId: practitionerId ?? "",
    handleGoToStep,
    handleCompleteOnboarding,
    handleCreateTemplate,
    handleUpdateTemplate,
    handleDeleteTemplate,
    handleRefreshMessageTemplates,
    isMessageTemplatesLoading,
    crmWhatsappWabaId,
    crmWhatsappChannelOption,
    crmSetupConfiguration,
    hasActiveMetaEndpoint: eligibility.hasActiveMetaEndpoint,
    isEndpointStatusLoading: eligibility.isLoading,
    hasFluxZapExtensionActive: eligibility.hasFluxZapExtensionActive,
    isFluxZapExtensionLoading: eligibility.isLoading,
    refetchFluxZapExtensionStatus: eligibility.refetch,
    markMessageTemplatesSectionOpened,
  };
}
